import { IconColor, IconSize } from '@aurora/shared-client/components/common/Icon/enums';
import Icon from '@aurora/shared-client/components/common/Icon/Icon';
import AppContext from '@aurora/shared-client/components/context/AppContext/AppContext';
import useDateTime from '@aurora/shared-client/components/useDateTime';
import useQueryWithTracing from '@aurora/shared-client/components/useQueryWithTracing';
import IconTypes from '@aurora/shared-client/public/static/graphics/processed/enums';
import type { User } from '@aurora/shared-generated/types/graphql-schema-types';
import { EndUserComponent } from '@aurora/shared-types/pages/enums';
import React, { useContext } from 'react';
import { useClassNameMapper } from 'react-bootstrap';
import type {
  BannedUserStatusQuery,
  BannedUserStatusQueryVariables
} from '../../../types/graphql-types';
import useTranslation from '../../useTranslation';
import bannedUserStatus from '../BannedUserStatus.query.graphql';
import localStyles from './BanMessageBanner.module.pcss';

interface Props {
  /**
   * Class name(s) to apply to the component element.
   */
  className?: string;
  /**
   * the banned user
   */
  user?: User;
}

/**
 * Ban Message Banner.
 * @author Elamaran P
 */
const BanMessageBanner: React.FC<React.PropsWithChildren<Props>> = ({ className, user }) => {
  const cx = useClassNameMapper(localStyles);
  const { formatMessage, loading: textLoading } = useTranslation(
    EndUserComponent.BAN_MESSAGE_BANNER
  );
  const { contextUser } = useContext(AppContext);

  const { data: banPoliciesData, loading: banPoliciesDataLoading } = useQueryWithTracing<
    BannedUserStatusQuery,
    BannedUserStatusQueryVariables
  >(module, bannedUserStatus, {
    variables: {
      userId: user?.id ?? contextUser?.id
    }
  });
  const { formatDate } = useDateTime();

  if (textLoading || banPoliciesDataLoading) {
    return null;
  }

  if (banPoliciesData == null) {
    return null;
  }

  const dateEnd = banPoliciesData?.banForUser?.dateEnd;

  const absoluteTime = formatDate(Number(dateEnd), {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric'
  });

  /**
   * Renders the banner for user when marked as ban.
   */
  function renderBannerForMessage(): React.ReactElement {
    return (
      <div className={cx('lia-user-status-container', className)} data-testid="BanMessageBanner">
        <span className={cx('lia-g-mr-10')}>
          <Icon icon={IconTypes.DangerIcon} color={IconColor.DANGER} size={IconSize.PX_16} />
        </span>
        <div>
          {formatMessage(
            dateEnd === '-1' ? 'banPermanent' : 'ban',
            dateEnd === '-1' ? {} : { date: absoluteTime }
          )}
        </div>
      </div>
    );
  }

  return renderBannerForMessage();
};

export default BanMessageBanner;
